import { getOrderValue } from '@/features/trade/order/utils/math';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import useSymbol from '@/hooks/use-symbol';
import { useAccountStore } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { OrderType } from '@/types';
import { formatNumber, parseDecimalToBigInt } from '@/utils/value-format';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'zustand';

import { OrderFormSchema } from '../order-form';

type Props = {
  scaleOrders: OrderFormSchema[];
};

export default function OrderValue({ scaleOrders }: Props) {
  const symbol = useSymbol();
  const form = useFormContext<OrderFormSchema>();

  const size = form.watch('order.size');
  const isBuy = form.watch('order.isBuy');
  const orderType = form.watch('order.orderType');
  const limitPrice = form.watch('order.limitPrice');

  const midPrice = useStore(
    useMarketStore(symbol),
    (state) => state.marketData.midPrice,
  );
  const bboPrice = useStore(
    useMarketStore(symbol),
    (state) =>
      (isBuy ? state.book.asks : state.book.bids).find(
        ([, quantity]) => quantity > 0n,
      )?.[0],
  );
  const isEval = useAccountStore((state) =>
    getIsStatusEval(state.getCapitalStatus(state.address)),
  );

  const orderValue = useMemo(() => {
    const { marketSpec } = useMarketStore(symbol).getState();

    if (orderType === OrderType.SCALE) {
      const orderValues = {
        bigint: 0n,
        decimal: 0,
      };

      scaleOrders.forEach((order) => {
        const orderValue = getOrderValue(
          symbol,
          parseDecimalToBigInt(order.order.size, marketSpec.sizeDecimals),
          parseDecimalToBigInt(
            order.order.limitPrice,
            marketSpec.priceDecimals,
          ),
        );

        orderValues.bigint += orderValue.bigint;
        orderValues.decimal += Number(orderValue.decimal);
      });

      return orderValues.decimal.toString();
    }

    const price =
      orderType === OrderType.MARKET
        ? isEval
          ? bboPrice
          : midPrice
        : parseDecimalToBigInt(limitPrice, marketSpec.priceDecimals);
    if (price === undefined) return;

    return getOrderValue(
      symbol,
      parseDecimalToBigInt(size, marketSpec.sizeDecimals),
      price,
    )?.decimal;
  }, [
    symbol,
    orderType,
    size,
    isEval,
    bboPrice,
    midPrice,
    limitPrice,
    scaleOrders,
  ]);

  if (!orderValue) return '-';

  return (
    formatNumber(orderValue, {
      digits: 2,
    }) + ' USD'
  );
}
